import { useState } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Tag,
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Dropdown,
  message,
  Modal,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
  DownloadOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { tradesApi } from '@tradermind/shared/api';
import type { Trade, TradeFilters } from '@tradermind/shared/types';
import { formatCurrency, formatDateTime } from '@tradermind/shared/utils';

const { RangePicker } = DatePicker;

export default function TradeListPage() {
  const { t } = useTranslation('trade');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<TradeFilters>({});
  const [pagination, setPagination] = useState({ page: 1, page_size: 20 });
  const [search, setSearch] = useState('');

  const deleteMutation = useMutation({
    mutationFn: (id: number) => tradesApi.deleteTrade(id),
    onSuccess: () => {
      message.success(t('messages.delete_success'));
      queryClient.invalidateQueries({ queryKey: ['trades'] });
    },
    onError: () => {
      message.error(t('messages.delete_failed'));
    },
  });

  const handleDelete = (trade: Trade) => {
    Modal.confirm({
      title: t('messages.delete_confirm_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('messages.delete_confirm_content', { symbol: trade.symbol }),
      okText: t('delete_trade'),
      okType: 'danger',
      cancelText: t('common:cancel'),
      onOk: () => deleteMutation.mutate(trade.id),
    });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['trades', filters, pagination, search],
    queryFn: () =>
      tradesApi.getTrades({
        ...filters,
        ...pagination,
        symbol: search || undefined,
      }),
  });

  const { data: statsData } = useQuery({
    queryKey: ['trade-stats'],
    queryFn: () => tradesApi.getTradeStats(),
  });

  const trades = data?.data?.items || [];
  const total = data?.data?.total || 0;
  const stats = statsData?.data;

  const handleExport = async () => {
    try {
      message.loading({ content: t('exporting'), key: 'export' });
      const blob = await tradesApi.exportTrades({
        ...filters,
        symbol: search || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trades_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success({ content: t('messages.export_success'), key: 'export' });
    } catch (error) {
      message.error({ content: t('messages.export_failed'), key: 'export' });
    }
  };

  const columns = [
    {
      title: t('columns.symbol'),
      dataIndex: 'symbol',
      key: 'symbol',
      render: (symbol: string, record: Trade) => (
        <Space>
          <span style={{ fontWeight: 500 }}>{symbol}</span>
          <Tag color={record.direction === 'long' ? 'green' : 'red'}>
            {record.direction === 'long' ? t('side.long') : t('side.short')}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('columns.entry_price'),
      dataIndex: 'entry_price',
      key: 'entry_price',
      render: (price: number) => price.toFixed(4),
    },
    {
      title: t('columns.exit_price'),
      dataIndex: 'exit_price',
      key: 'exit_price',
      render: (price: number | undefined) => (price ? price.toFixed(4) : '-'),
    },
    {
      title: t('columns.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: t('columns.pnl'),
      dataIndex: 'profit_loss',
      key: 'profit_loss',
      render: (pl: number | undefined) =>
        pl !== undefined ? (
          <span style={{ color: pl >= 0 ? '#10B981' : '#EF4444', fontWeight: 500 }}>
            {pl >= 0 ? '+' : ''}
            {formatCurrency(pl)}
          </span>
        ) : (
          '-'
        ),
    },
    {
      title: t('r_multiple'),
      dataIndex: 'r_multiple',
      key: 'r_multiple',
      render: (r: number | undefined | null) =>
        r !== undefined && r !== null ? (
          <Tag color={r > 0 ? 'green' : r < 0 ? 'red' : 'default'} style={{ fontWeight: 500 }}>
            {r > 0 ? '+' : ''}{r}R
          </Tag>
        ) : (
          '-'
        ),
    },
    {
      title: t('columns.quality_score'),
      dataIndex: 'quality_score',
      key: 'quality_score',
      render: (score: number | undefined) =>
        score !== undefined ? (
          <Tag color={score >= 80 ? 'green' : score >= 60 ? 'orange' : 'red'}>{score}{t('columns.score_unit')}</Tag>
        ) : (
          '-'
        ),
    },
    {
      title: t('columns.open_time'),
      dataIndex: 'open_time',
      key: 'open_time',
      render: (time: string) => formatDateTime(time),
    },
    {
      title: t('columns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'open' ? 'blue' : 'default'}>
          {status === 'open' ? t('status.open') : t('status.closed')}
        </Tag>
      ),
    },
    {
      title: t('columns.action'),
      key: 'action',
      render: (_: unknown, record: Trade) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                icon: <EyeOutlined />,
                label: t('view_details'),
                onClick: () => navigate(`/trades/${record.id}`),
              },
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: t('edit_trade'),
                onClick: () => navigate(`/trades/${record.id}/edit`),
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: t('delete_trade'),
                danger: true,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleDelete(record);
                },
              },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title={t('stats.total_trades')} value={stats?.total_trades || 0} suffix={t('stats.trades_unit')} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={t('stats.total_pnl')}
              value={stats?.net_pl ?? stats?.total_profit_loss ?? 0}
              precision={2}
              prefix="$"
              valueStyle={{
                color: (stats?.net_pl ?? stats?.total_profit_loss ?? 0) >= 0 ? '#10B981' : '#EF4444',
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={t('stats.total_commission')}
              value={(stats?.total_commission ?? 0) + (stats?.total_swap ?? 0)}
              precision={2}
              prefix="$"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={t('stats.win_rate')}
              value={stats?.win_rate || 0}
              precision={1}
              suffix="%"
              valueStyle={{ color: (stats?.win_rate || 0) >= 50 ? '#10B981' : '#EF4444' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={t('stats.win_loss_ratio')}
              value={stats?.win_loss_ratio ?? 0}
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={t('stats.profit_factor')}
              value={stats?.profit_factor ?? 0}
              precision={2}
              valueStyle={{
                color: (stats?.profit_factor ?? 0) >= 1 ? '#10B981' : '#EF4444',
              }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Input
              placeholder={t('search_symbol')}
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              placeholder={t('pnl_status')}
              allowClear
              style={{ width: 120 }}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              options={[
                { value: 'profit', label: t('status.profit') },
                { value: 'loss', label: t('status.loss') },
                { value: 'open', label: t('status.open') },
              ]}
            />
            <RangePicker
              onChange={(dates) => {
                if (dates) {
                  setFilters({
                    ...filters,
                    start_date: dates[0]?.format('YYYY-MM-DD'),
                    end_date: dates[1]?.format('YYYY-MM-DD'),
                  });
                } else {
                  setFilters({ ...filters, start_date: undefined, end_date: undefined });
                }
              }}
            />
          </Space>
          <Space>
            <Button icon={<UploadOutlined />} onClick={() => navigate('/trades/import')}>
              {t('import')}
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>{t('export')}</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/trades/new')}>
              {t('new_trade')}
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={trades}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.page_size,
            total,
            showSizeChanger: true,
            showTotal: (total) => t('total_count', { count: total }),
            onChange: (page, pageSize) => setPagination({ page, page_size: pageSize }),
          }}
          onRow={(record) => ({
            onClick: () => navigate(`/trades/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
    </div>
  );
}
